import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE } from "../api/config";
import "./WarehouseTransferIssueActionBox.css";

function norm(value) {
  return String(value || "").trim().toUpperCase();
}

function pick(row, ...keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return "";
}

function getType(row) {
  return norm(
    pick(row, "discrepancyType", "discrepancy_type", "notificationType", "notification_type", "type")
  );
}

function getStatus(row) {
  return norm(pick(row, "status"));
}

function getFrom(row) {
  return norm(pick(row, "fromLocationCode", "from_location_code", "fromShop", "from_shop", "from"));
}

function getTo(row) {
  return norm(pick(row, "toLocationCode", "to_location_code", "toShop", "to_shop", "to"));
}

function getVoucher(row) {
  return pick(row, "voucherNo", "voucher_no", "transferNo", "transfer_no", "transferId", "transfer_id", "id") || "-";
}

function getIssueId(row) {
  return pick(row, "id", "entityId", "entity_id", "discrepancyId", "discrepancy_id");
}

function getRaw(row) {
  return row?.raw && typeof row.raw === "object" ? row.raw : row;
}

function isClosedStatus(status) {
  const s = norm(status);
  return (
    s === "ACCEPTED" ||
    s.includes("CLOSED") ||
    s.includes("COMPLETED") ||
    s.includes("STOCK_POSTED") ||
    s.includes("RETURN_RECEIVED") ||
    s.includes("REJECTED_RETURN_KEEP_RECEIVER")
  );
}

function itemText(row) {
  const raw = getRaw(row);
  const code =
    pick(raw, "received_product_code", "receivedProductCode", "expected_product_code", "expectedProductCode") || "-";
  const color =
    pick(raw, "received_color", "receivedColor", "expected_color", "expectedColor") || "-";
  const size =
    pick(raw, "received_size", "receivedSize", "expected_size", "expectedSize") || "-";
  const qty =
    pick(raw, "received_qty", "receivedQty", "expected_qty", "expectedQty") || "-";

  return `${code} / ${color} / ${size} / Qty ${qty}`;
}

function canExtraAction(row, warehouse) {
  const type = getType(row);
  const status = getStatus(row);
  const from = getFrom(row);

  if (from !== norm(warehouse)) return false;
  if (!type.includes("EXTRA")) return false;
  if (isClosedStatus(status)) return false;

  return (
    status === "EXTRA_ITEM_PENDING_SENDER_DECISION" ||
    status === "EXTRA_ITEM_REPORTED" ||
    status === "SENDER_CONFIRM_PENDING" ||
    status.includes("PENDING_SENDER") ||
    status.includes("SENDER_CONFIRM")
  );
}

function canWrongConfirm(row, warehouse) {
  const type = getType(row);
  const status = getStatus(row);
  const from = getFrom(row);

  if (from !== norm(warehouse)) return false;
  if (!type.includes("WRONG")) return false;
  if (isClosedStatus(status)) return false;

  return (
    status === "OPEN" ||
    status === "UNKNOWN_PENDING_REVIEW" ||
    status === "WRONG_ITEM_PENDING_SENDER_CONFIRM" ||
    status.includes("PENDING") ||
    status.includes("REVIEW")
  );
}

function isMissingReview(row, warehouse) {
  const type = getType(row);
  const status = getStatus(row);
  const from = getFrom(row);

  if (from !== norm(warehouse)) return false;
  if (isClosedStatus(status)) return false;

  return type.includes("MISSING") || type.includes("SHORT");
}

export default function WarehouseTransferIssueActionBox({ user, selectedLocation }) {
  const warehouse = norm(selectedLocation);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState("");
  const [message, setMessage] = useState("");

  const loadRows = useCallback(async () => {
    if (!warehouse) return;

    try {
      setLoading(true);

      const res = await fetch(
        `${API_BASE}/api/erp/pos/transfer-notifications/${encodeURIComponent(warehouse)}?limit=100`,
        { cache: "no-store" }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        setRows([]);
        return;
      }

      const list = Array.isArray(data.rows) ? data.rows : [];

      const filtered = list.filter((row) => {
        const entityType = norm(row.entityType || row.entity_type);
        const kind = norm(row.kind);
        const from = getFrom(row);

        if (entityType !== "TRANSFER_DISCREPANCY" && kind !== "ISSUE") return false;
        if (from !== warehouse) return false;
        if (isClosedStatus(getStatus(row))) return false;

        return canExtraAction(row, warehouse) || canWrongConfirm(row, warehouse) || isMissingReview(row, warehouse);
      });

      console.log("WAREHOUSE_TRANSFER_ISSUE_BOX_FORCE_VISIBLE_IN_OFFICE_TAB_V2", {
        warehouse,
        apiRows: list.length,
        filteredRows: filtered.length,
        firstFiltered: filtered[0],
      });

      window.__WAREHOUSE_TRANSFER_ISSUE_DEBUG__ = {
        warehouse,
        apiRows: list,
        filteredRows: filtered,
      };

      setRows(filtered);
    } catch (err) {
      console.error("Warehouse transfer issue actions load failed:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [warehouse]);

  useEffect(() => {
    loadRows();
    const timer = setInterval(loadRows, 10000);
    return () => clearInterval(timer);
  }, [loadRows]);

  const count = rows.length;

  async function postExtra(row, action) {
    const id = getIssueId(row);
    const isConfirm = action === "confirm";

    const confirmText = isConfirm
      ? "ဒီ Extra Item ကို Warehouse/source ဘက်က ပို့ထားတာဟုတ်ကြောင်း Confirm လုပ်မည်။ Warehouse stock -1 ဖြစ်မည်။"
      : "ဒီ Extra Item က Warehouse/source ဘက်က မဟုတ်ကြောင်း Reject လုပ်မည်။ Office assign source flow ကို ဆက်သွားမည်။";

    if (!window.confirm(confirmText)) return;

    try {
      setLoadingId(id);
      setMessage("");

      const endpoint = isConfirm
        ? "/api/erp/pos/transfer-discrepancy/extra-item-sender-confirm"
        : "/api/erp/pos/transfer-discrepancy/extra-item-sender-reject";

      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          discrepancyId: id,
          shop: warehouse,
          shopCode: warehouse,
          confirmedBy: user?.username || warehouse,
          rejectedBy: user?.username || warehouse,
          note: confirmText,
          reason: confirmText,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.message || "Extra item action failed");

      setMessage(data.message || "Success");
      await loadRows();
    } catch (err) {
      setMessage(err.message || "Extra item action failed");
    } finally {
      setLoadingId("");
    }
  }

  async function confirmWrong(row) {
    const id = getIssueId(row);

    const confirmText =
      "ဒီ Wrong Item ကို Warehouse/source ဘက်က မှားပို့ထားတာဟုတ်ကြောင်း Confirm လုပ်မည်။ Stock adjustment လုပ်ပြီး receiver inventory ထဲဝင်မည်။";

    if (!window.confirm(confirmText)) return;

    try {
      setLoadingId(id);
      setMessage("");

      const res = await fetch(`${API_BASE}/api/erp/office/transfer-discrepancy/wrong-item-return-decision`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          discrepancyId: id,
          decision: "REJECT_RETURN_KEEP_RECEIVER",
          approvedBy: user?.username || warehouse,
          user: user?.username || warehouse,
          note: confirmText,
          officeNote: confirmText,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.message || "Wrong item confirm failed");

      setMessage(data.message || "Wrong item confirmed and stock adjusted");
      await loadRows();
    } catch (err) {
      setMessage(err.message || "Wrong item confirm failed");
    } finally {
      setLoadingId("");
    }
  }

  // WAREHOUSE_TRANSFER_ISSUE_BOX_FORCE_VISIBLE_IN_OFFICE_TAB_V2
  if (!warehouse) return null;

  return (
    <section className="warehouseIssueBox">
      <div className="warehouseIssueBox__head">
        <div>
          <span>TRANSFER ISSUE ACTIONS</span>
          <h2>Warehouse Issue Check ({count})</h2>
          <p>
            Office → Warehouse → Shop transfer မှာ receiver shop က report တင်ထားတဲ့
            Wrong / Extra / Missing item များကို warehouse ဘက်မှ စစ်ရန်။
          </p>
        </div>

        <div className="warehouseIssueBox__headActions">
          <button type="button" onClick={loadRows} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
          <button type="button" onClick={() => setOpen((prev) => !prev)}>
            {open ? "Hide" : "Open"}
          </button>
        </div>
      </div>

      {message && <div className="warehouseIssueBox__message">{message}</div>}

      {open && (
        <div className="warehouseIssueBox__list">
          {rows.length === 0 && (
            <div className="warehouseIssueCard">
              <div className="warehouseIssueCard__top">
                <div>
                  <b>No pending warehouse issue</b>
                  <span>{warehouse}</span>
                </div>
                <em>DEBUG READY</em>
              </div>
              <div className="warehouseIssueCard__body">
                <p>Extra / Wrong / Missing issue pending ရှိလာရင် ဒီနေရာမှာပေါ်မယ်။</p>
                <p>Browser console မှာ window.__WAREHOUSE_TRANSFER_ISSUE_DEBUG__ နဲ့ စစ်နိုင်ပါတယ်။</p>
              </div>
            </div>
          )}

          {rows.map((row) => {
            const id = getIssueId(row);
            const type = getType(row);
            const status = getStatus(row);
            const busy = loadingId === id;

            return (
              <div key={id} className="warehouseIssueCard">
                <div className="warehouseIssueCard__top">
                  <div>
                    <b>{type.replaceAll("_", " ")}</b>
                    <span>{getVoucher(row)}</span>
                  </div>
                  <em>{status.replaceAll("_", " ")}</em>
                </div>

                <div className="warehouseIssueCard__body">
                  <p>
                    <b>Route:</b> {getFrom(row)} → {getTo(row)}
                  </p>
                  <p>
                    <b>Item:</b> {itemText(row)}
                  </p>
                  <p>
                    <b>Reason:</b> {pick(getRaw(row), "reason", "note") || row.message || "-"}
                  </p>
                </div>

                <div className="warehouseIssueCard__actions">
                  {canWrongConfirm(row, warehouse) && (
                    <button type="button" disabled={busy} onClick={() => confirmWrong(row)}>
                      {busy ? "Saving..." : "Confirm Wrong Item"}
                    </button>
                  )}

                  {canExtraAction(row, warehouse) && (
                    <>
                      <button type="button" disabled={busy} onClick={() => postExtra(row, "confirm")}>
                        {busy ? "Saving..." : "Confirm Extra Sent"}
                      </button>
                      <button
                        type="button"
                        className="danger"
                        disabled={busy}
                        onClick={() => postExtra(row, "reject")}
                      >
                        {busy ? "Saving..." : "Reject / Not From Warehouse"}
                      </button>
                    </>
                  )}

                  {isMissingReview(row, warehouse) && (
                    <span className="warehouseIssueCard__pending">
                      Missing item — Office / Warehouse review pending
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
